import {PageTitle} from '_metronic/layout/core'
import {FC, useState, useEffect, Fragment, useRef} from 'react'
import HTMLRenderer from 'react-html-renderer'
import ReactQuill from 'react-quill'
import Api from '../../../thesis-and-dissertations/helpers/api'

import './style.css'
import UploadModal from './UploadModal'
import AlertModal from './AlertModal'
import TemaEssayModal from './TemaEssayModal'

import handleApiError from 'thesis-and-dissertations/helpers/handleApiError'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'
const api = new Api()

type Props = {
  step: number
  showModal: boolean
  handleShowModalAlert: any
  handleStepData: any
  errorProps: any
  asTeam: boolean // Deprecated - always false now (individu only)
  fieldConfig?: any // Konfigurasi field dari API (upload_proposal, upload_ppt)
  enabledSteps?: number[]
  currentStep?: number
}

const Step3Page: React.FC<Props> = ({
  step,
  showModal,
  handleShowModalAlert,
  handleStepData,
  errorProps,
  asTeam,
  fieldConfig,
  enabledSteps = [3, 4, 5],
  currentStep = 3,
}) => {
  const navigate = useNavigate()

  const [fieldRefs, setFieldRefs] = useState<any>({
    judul_proposal: useRef(null),
    ringkasan_proposal: useRef(null),
    file_proposal: useRef(null),
    file_ppt: useRef(null),
    file_kesehatan: useRef(null),
    file_kerja: useRef(null),
  })
  const [showModalUpload, setShowModalUpload] = useState(false)
  const [showModalAlert, setShowModalAlert] = useState(false)
  const [showModalTemaDetail, setShowModalTemaDetail] = useState(false)
  const [selectedTemaId, setSelectedTemaId] = useState<string | null>(null)
  const [temaEssayList, setTemaEssayList] = useState<any[]>([])
  const [errors, setErrors] = useState<any>({})
  const [formData, setFormData] = useState<any>({})
  const [typeUpload, setTypeUpload] = useState<string>('')
  const [priority, setPriority] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [dataPana, setDataPana] = useState<any>({})
  const [proposalLength, setProposalLength] = useState({
    ringkasan_proposal: 0,
  })

  useEffect(() => {
    getPana()
    getTemaEssayList()
  }, [])

  useEffect(() => {
    if (!errorProps) {
      return
    }
    
    scrollToElement(errorProps)
  }, [errorProps])

  useEffect(() => {
    setShowModalAlert(showModal)
  }, [showModal])

  useEffect(() => {
    handleStepData({
      step: step,
      tipe: 'individu', // Always individu, Step 1 & 2 are skipped
      judul_proposal: formData?.judul_proposal,
      ringkasan_proposal: formData?.ringkasan_proposal,
    })
  }, [formData])

  const getPlainTextLength = (htmlString: any) => {
    if (typeof htmlString !== 'string') return 0
    const div = document.createElement('div')
    div.innerHTML = htmlString
    const text = div.textContent || div.innerText || ''
    return text.trim().length
  }

  const handleShowModalUpload = (action: boolean, type: string, priority: number) => {
    setShowModalUpload(action)
    setTypeUpload(type)
    setPriority(priority)

    if (action == false) {
      getPana(true) // Preserve existing form data (essay yang sedang diketik)
    }
  }

  const handleInput = (e: any, name: string) => {
    const value = e?.target?.value ?? e
    setFormData({...formData, [name]: value})
    setErrors({})

    // Update character count untuk ringkasan_proposal
    if (name == 'ringkasan_proposal') {
      setProposalLength({
        ringkasan_proposal: getPlainTextLength(value),
      })
    }
  }

  const getPana = (preserveFormData: boolean = false) => {
    setLoading(true)
    api
      .getRegisterPana()
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data
        
        setDataPana(data)

        // Jika preserveFormData = true (dipanggil dari upload file),
        // hanya update dataPana untuk file info, jangan override formData
        if (preserveFormData) {
          // Tetap gunakan formData yang ada (essay yang baru diketik)
          // Hanya update character count jika ringkasan_proposal ada di formData
          if (formData?.ringkasan_proposal) {
            setProposalLength({
              ringkasan_proposal: getPlainTextLength(formData.ringkasan_proposal),
            })
          }
          return
        }

        // Jika preserveFormData = false (initial load), ambil data dari backend
        let dataForm = {
          step: step,
          judul_proposal: data?.judul_proposal || '',
          ringkasan_proposal: data?.ringkasan_proposal || '',
        }

        setProposalLength({
          ringkasan_proposal: getPlainTextLength(data?.ringkasan_proposal || ''),
        })

        setFormData(dataForm)
        handleStepData(dataForm)
      })
      .catch((error: any) => {
        setLoading(false)
        const {data, status} = error.response

        handleApiError(error, navigate)
        toast.error(data?.meta?.message)
        setErrors(data?.meta?.error)
      })
  }

  const getTemaEssayList = () => {
    api
      .getTemaEssayList()
      .then((response: any) => {
        // Response structure: { meta: {...}, data: { current_page, data: [...] } }
        const temaData = response?.data?.data?.data || response?.data?.data || []
        setTemaEssayList(temaData)
      })
      .catch((error: any) => {
        console.error('Error fetching tema essay:', error)
        setTemaEssayList([])
      })
  }

  const handleShowTemaDetail = (temaTitle: string) => {
    // Cari ID berdasarkan title yang dipilih
    const selectedTema = temaEssayList.find((tema: any) => 
      (tema.title || tema.nama || tema.judul) == temaTitle
    )
    if (selectedTema) {
      setSelectedTemaId(selectedTema.id)
      setShowModalTemaDetail(true)
    }
  }

  const handleCloseTemaDetail = () => {
    setShowModalTemaDetail(false)
    setSelectedTemaId(null)
  }

  const handleFinish = (data: any) => {
    handleStepData(data)
    setShowModalAlert(false)
    handleShowModalAlert(false)
  }

  const scrollToElement = (errors: any) => {
    const firstErrorField = Object.keys(errors)[0]
    const target = fieldRefs[firstErrorField]

    if (target && target.current) {
      const yOffset = -80 // jika ada header
      const y = target.current.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({top: y, behavior: 'smooth'})
    }
  }

  return (
    <>
      <UploadModal
        showModalUpload={showModalUpload}
        handleShowModalUpload={handleShowModalUpload}
        typeUpload={typeUpload}
        priority={priority}
      />

      <AlertModal
        showModal={showModalAlert}
        handleShowModalAlert={handleShowModalAlert}
        handleStepData={handleFinish}
      />

      <TemaEssayModal
        showModal={showModalTemaDetail}
        handleShowModal={handleCloseTemaDetail}
        temaId={selectedTemaId}
      />

      <div className='py-8 px-15'>
        <div className='w-20px'>
          <div
            className='d-flex justify-content-center text-primary rounded-circle px-4 py-1'
            style={{background: 'var(--Primary-L-50, #F9EAE8)'}}
          >
            {enabledSteps.indexOf(currentStep) + 1}
          </div>
        </div>

        <p className='h4 mt-8' style={{color: '#64738D'}}>
          Ringkasan Essay
        </p>

        <div className='d-flex mt-5 justify-content-space-between'>
          {enabledSteps.map((stepNum, index) => {
            const currentStepIndex = enabledSteps.indexOf(currentStep)
            const isCompleted = index <= currentStepIndex
            return (
              <div key={stepNum} className={`progress-step-container ${index > 0 ? 'px-2' : ''}`}>
                <div className={`progress h-7px rounded ${isCompleted ? 'bg-primary' : 'bg-secondary'}`}>
                  <div className={`progress-bar rounded ${isCompleted ? 'bg-primary' : 'bg-secondary'} fs-7 fw-bold`} role='progressbar' />
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-5'>
          <div className='row'>
            {step == 5 ? (
              <>
                {/* <div className='col-md-12'>
                  <p className='h6 mb-3 mt-5'>Anda telah memilih Daerah dan Bidang Keilmuan</p>

                  {dataPana?.pendaftaran_locus_temas?.length > 0 && (
                    <div className='card border p-5 mt-4'>
                      <div>
                        <div className='mb-2'>
                          <p>
                            {dataPana?.pendaftaran_locus_temas?.[0]?.locus?.parent_locus
                              ?.tema_program ?? ''}{' '}
                            -{' '}
                            <span className='fw-bold'>
                              {dataPana?.pendaftaran_locus_temas?.[0]?.locus?.parent_locus?.nama ??
                                ''}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className='border-top' />

                      <div className='row'>
                        <div className={`col-md-${dataPana?.is_selesai == 1 ? '12' : '8'}`}>
                          <p className='mt-5 fw-bold'>Bidang Keilmuan/Keahlian Terkait</p>
                          <p>
                            {dataPana?.pendaftaran_locus_temas?.[0]?.locus?.parent_locus
                              ?.bidang_keilmuan ?? ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className='col-md-12'>
                  <p className='h6 mb-3 mt-5'>
                    Anda telah memilih lokasi Program Pemberdayaan masyarakat
                  </p>

                  {dataPana?.pendaftaran_locus_temas?.map((item: any) => {
                    return (
                      <div
                        className='d-flex justify-content-between border rounded p-3 mt-4'
                        style={{cursor: 'pointer'}}
                      >
                        <p>
                          <i className='fa fa-map-marker-alt text-primary' /> {item?.locus?.locus}
                        </p>
                      </div>
                    )
                  })}
                </div>
                <div className='col-md-12'>
                  <p className='h6 mb-3 mt-5'>Anda telah memilih Kearifan Lokal</p>
                  {dataPana?.pendaftaran_locus_temas?.map(
                    (item: any) => (
                      <div
                        className='d-flex justify-content-between border rounded p-3 mt-4'
                        style={{cursor: 'pointer'}}
                      >
                        <div>
                          <div className='m-2'>
                            <HTMLRenderer html={item.locus.tema?.tema ?? ''} />
                          </div>

                          {item.locus.tema?.indikator_pengabdian?.length > 0 ? (
                            <div className='m-2'>
                              <p>Indikasi kegiatan yang dapat dilakukan peserta seperti:</p>
                              <ol type='a'>
                                {item.locus.tema?.indikator_pengabdian?.map((indicators: any) => (
                                  <li>{indicators?.indikator_pengabdian}</li>
                                ))}
                              </ol>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  )}
                </div> */}
              </>
            ) : null}

            <div className='col-md-12'>
              <p
                className='h6 mb-3 mt-5'
                ref={fieldRefs.judul_proposal}
                style={{scrollMarginTop: '100px'}}
              >
                Tema Essay <i className='text-danger'>*</i>
              </p>

              <div className='d-flex gap-2'>
                <select
                  className='form-select'
                  disabled={step == 5 || asTeam}
                  value={formData?.judul_proposal || ''}
                  onChange={(e) => handleInput(e, 'judul_proposal')}
                >
                  <option value=''>Pilih Tema Essay</option>
                  {temaEssayList.map((tema: any) => {
                    const displayTitle = tema.title || tema.nama || tema.judul || tema.name || tema.tema || `ID: ${tema.id}`
                    return (
                      <option key={tema.id} value={displayTitle}>
                        {displayTitle}
                      </option>
                    )
                  })}
                </select>
                
                {formData?.judul_proposal && (
                  <button
                    type='button'
                    className='btn btn-outline-primary flex-shrink-0'
                    onClick={() => handleShowTemaDetail(formData?.judul_proposal)}
                    disabled={step == 5}
                  >
                    <i className='bi bi-info-circle me-1'></i>
                    Detail
                  </button>
                )}
              </div>

              {errorProps?.judul_proposal ? (
                <p className='text-danger mt-1'>{errorProps?.judul_proposal}</p>
              ) : null}
            </div>

            <div className='col-md-12'>
              <p className='h6 mb-3 mt-5' ref={fieldRefs.ringkasan_proposal}>
                Isi Essay
                <i className='text-danger'>*</i>
              </p>

              <ReactQuill
                theme='snow'
                onChange={(value) => {
                  handleInput(value, 'ringkasan_proposal')
                }}
                placeholder='Tuliskan isi essay kamu di sini.'
                readOnly={step == 5 || asTeam}
                value={formData?.ringkasan_proposal || ''}
                className='quill-editor'
              />

              <p
                style={{
                  color: proposalLength?.ringkasan_proposal < 1000 || proposalLength?.ringkasan_proposal > 3000 ? 'red' : 'blue',
                  fontSize: '10px',
                }}
                className='mt-2'
              >
                Jumlah karakter yang telah diinput {proposalLength?.ringkasan_proposal} karakter.
              </p>

              <p style={{color: '#667085', fontSize: '10px'}} className='mt-2'>
                Tuliskan essay kamu dengan jelas dan terstruktur, minimal 1000 karakter dan maksimal 3000
                karakter.
              </p>

              {errorProps?.ringkasan_proposal ? (
                <p className='text-danger mt-1'>{errorProps?.ringkasan_proposal}</p>
              ) : null}
            </div>

            <div className='row'>
              {/* Upload Proposal - tampilkan jika fieldConfig tidak ada atau upload_proposal.required !== false */}
              {(!fieldConfig || fieldConfig?.upload_proposal?.required !== false) && (
                <div className='col-md-6'>
                  <div className='col-md-12 mt-8'>
                    <p className='h6 mb-3' ref={fieldRefs.file_proposal}>
                      Upload Proposal <i className='text-danger'>*</i>
                    </p>

                    {dataPana?.file_proposal_prioritas_1?.file ? (
                      <a
                        href={dataPana?.file_proposal_prioritas_1?.file}
                        style={{color: '#575757', textDecoration: 'none'}}
                        target='_blank'
                      >
                        <div
                          className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                          style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                        >
                          <div className='fw-bold'>
                            <i className='fa fa-file-alt text-black me-2' />
                            File Proposal {dataPana?.file_proposal_prioritas_1?.nama_file}
                          </div>
                          <div>
                            <i className='fa fa-check-circle text-success' />
                          </div>
                        </div>
                      </a>
                    ) : null}

                    {!asTeam ? (
                      step !== 5 ? (
                        <>
                          <div
                            onClick={() => handleShowModalUpload(true, 'proposal', 1)}
                            className='d-flex justify-content-between border rounded p-4 mt-4'
                            style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                          >
                            <p className='fw-bold'>
                              <i className='bi bi-upload text-black' /> Upload Proposal
                            </p>
                          </div>

                          <div style={{color: '#667085', fontSize: '11px'}} className='mt-2'>
                            <p>
                              Klik di sini untuk mendapatkan template proposal yang telah disiapkan.{' '}
                              <span className='fw-bold'>
                                {' '}
                                <a href={`${dataPana?.template_files?.proposal}`} target='_blank'>
                                  Unduh Template Proposal
                                </a>
                              </span>
                            </p>
                            <p>Notes: ukuran file max 50 MB, format file pdf</p>
                          </div>

                          {errorProps?.file_proposal ? (
                            <p className='text-danger mt-1'>{errorProps?.file_proposal}</p>
                          ) : null}
                        </>
                      ) : null
                    ) : null}
                  </div>
                </div>
              )}

              {/* Upload PPT - tampilkan jika fieldConfig tidak ada atau upload_ppt.required !== false */}
              {(!fieldConfig || fieldConfig?.upload_ppt?.required !== false) && (
                <div className='col-md-6'>
                  <div className='col-md-12 mt-8'>
                    <p className='h6 mb-3' ref={fieldRefs.file_Ppt_prioritas_1}>
                      Upload BAHAN PRESENTASI <i className='text-danger'>*</i>
                    </p>

                    {dataPana?.file_Ppt_prioritas_1?.file ? (
                      <a
                        href={dataPana?.file_Ppt_prioritas_1?.file}
                        style={{color: '#575757'}}
                        target='_blank'
                      >
                        <div
                          className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                          style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                        >
                          <div className='fw-bold'>
                            <i className='fa fa-file-alt text-black' /> File BAHAN PRESENTASI{' '}
                            {dataPana?.file_Ppt_prioritas_1?.nama_file}
                          </div>
                          <div>
                            <i className='fa fa-check-circle text-success' />
                          </div>
                        </div>
                      </a>
                    ) : null}

                    {!asTeam ? (
                      step !== 5 ? (
                        <>
                          <div
                            onClick={() => handleShowModalUpload(true, 'ppt', 1)}
                            className='d-flex justify-content-between border rounded p-4 mt-4'
                            style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                          >
                            <p className='fw-bold'>
                              <i className='bi bi-upload text-black' /> Upload BAHAN PRESENTASI
                            </p>
                          </div>

                          <div style={{color: '#667085', fontSize: '11px'}} className='mt-2'>
                            <p>
                              Klik di sini untuk mendapatkan template Bahan Presentasi yang telah
                              disiapkan.{' '}
                              <span className='fw-bold'>
                                {' '}
                                <a href={`${dataPana?.template_files?.ppt}`} target='_blank'>
                                  Unduh Template Presentasi
                                </a>
                              </span>
                            </p>
                            <p>Notes: ukuran file max 50 MB, format file pdf</p>
                          </div>

                          {errorProps?.file_ppt ? (
                            <p className='text-danger mt-1'>{errorProps?.file_ppt}</p>
                          ) : null}
                        </>
                      ) : null
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className='col-md-12 mt-15'>
              <p className='h6 mb-3'>
                Surat Keterangan Kesehatan <i className='text-danger'>*</i>
              </p>

              {dataPana?.fileKesehatan?.file ? (
                <a
                  href={dataPana?.fileKesehatan?.file}
                  style={{color: '#575757', textDecoration: 'none'}}
                  target='_blank'
                >
                  <div
                    className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                    style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                  >
                    <div className='fw-bold'>
                      <i className='fa fa-file-alt text-black me-2' />
                      File Surat Keterangan Kesehatan {dataPana?.file_kesehatan?.nama_file}
                    </div>
                    <div>
                      <i className='fa fa-check-circle text-success' />
                    </div>
                  </div>
                </a>
              ) : null}

              {step !== 5 ? (
                <>
                  <div
                    onClick={() => handleShowModalUpload(true, 'kesehatan', 1)}
                    className='d-flex justify-content-between border rounded p-4 mt-4'
                    style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                  >
                    <p className='fw-bold' ref={fieldRefs.file_kesehatan}>
                      <i className='bi bi-upload text-black' /> Upload Keterangan Kesehatan
                    </p>
                  </div>

                  <div style={{color: '#667085', fontSize: '11px'}} className='mt-2'>
                    <p>Ukuran file max 1 MB, format file pdf</p>
                  </div>

                  {errorProps?.file_kesehatan ? (
                    <p className='text-danger mt-1'>{errorProps?.file_kesehatan}</p>
                  ) : null}
                </>
              ) : null}
            </div>

            {dataPana?.is_kerja == 1 ? (
              <div className='col-md-12 mt-15'>
                <p className='h6 mb-3'>
                  Surat Keterangan Izin dari Tempat Kerja* <i className='text-danger'>*</i>
                </p>

                {dataPana?.fileKerja?.file ? (
                  <a
                    href={dataPana?.fileKerja?.file}
                    style={{color: '#575757', textDecoration: 'none'}}
                    target='_blank'
                  >
                    <div
                      className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                      style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                    >
                      <div className='fw-bold'>
                        <i className='fa fa-file-alt text-black me-2' />
                        File Surat Keterangan Izin dari Tempat Kerja{' '}
                        {dataPana?.file_kerja?.nama_file}
                      </div>
                      <div>
                        <i className='fa fa-check-circle text-success' />
                      </div>
                    </div>
                  </a>
                ) : null}

                {step !== 5 ? (
                  <>
                    <div
                      onClick={() => handleShowModalUpload(true, 'kerja', 1)}
                      className='d-flex justify-content-between border rounded p-4 mt-4'
                      style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                    >
                      <p className='fw-bold' ref={fieldRefs.file_kerja}>
                        <i className='bi bi-upload text-black' /> Upload Surat Keterangan Izin dari
                        Tempat Kerja
                      </p>
                    </div>

                    <div style={{color: '#667085', fontSize: '11px'}} className='mt-2'>
                      <p>
                        Klik di sini untuk mendapatkan template Surat Keterangan Izin dari Tempat
                        Kerja yang telah disiapkan.{' '}
                        <span className='fw-bold'>
                          {' '}
                          <a href={`${dataPana?.template_files?.surat_kerja}`} target='_blank'>
                            Unduh Template Surat Keterangan Izin dari Tempat Kerja
                          </a>
                        </span>
                      </p>
                      <p>Notes: ukuran file max 50 MB, format file pdf</p>
                    </div>

                    {errorProps?.file_kerja ? (
                      <p className='text-danger mt-1'>{errorProps?.file_kerja}</p>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {step !== 5 && dataPana?.fileKomitmen?.file ? (
              <div className='col-md-12 mt-15'>
                <p className='h6 mb-3'>
                  Surat Pernyataan Bermaterai <i className='text-danger'>*</i>
                </p>

                <a
                  href={dataPana?.fileKomitmen?.file}
                  style={{color: '#575757', textDecoration: 'none'}}
                  target='_blank'
                >
                  <div
                    className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                    style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                  >
                    <div className='fw-bold'>
                      <i className='fa fa-file-alt text-black me-2' />
                      File Surat Pernyataan Bermaterai
                    </div>
                    <div>
                      <i className='fa fa-check-circle text-success' />
                    </div>
                  </div>
                </a>
              </div>
            ) : null}

            {step == 5 ? (
              <div className='col-md-12 mt-15'>
                <p className='h6 mb-3'>
                  Surat Pernyataan Bermaterai <i className='text-danger'>*</i>
                </p>

                {dataPana?.fileKomitmen?.file ? (
                  <a
                    href={dataPana?.fileKomitmen?.file}
                    style={{color: '#575757', textDecoration: 'none'}}
                    target='_blank'
                  >
                    <div
                      className='d-flex justify-content-between align-items-center border rounded p-4 mt-4'
                      style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
                    >
                      <div className='fw-bold'>
                        <i className='fa fa-file-alt text-black me-2' />
                        Surat Pernyataan Bermaterai
                      </div>
                      <div>
                        <i className='fa fa-check-circle text-success' />
                      </div>
                    </div>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

const Step3: React.FC<Props> = ({
  step,
  showModal,
  handleShowModalAlert,
  handleStepData,
  errorProps,
  asTeam,
  fieldConfig,
  enabledSteps,
  currentStep,
}) => {
  return (
    <Step3Page
      step={step}
      showModal={showModal}
      handleShowModalAlert={handleShowModalAlert}
      handleStepData={handleStepData}
      errorProps={errorProps}
      asTeam={asTeam}
      fieldConfig={fieldConfig}
      enabledSteps={enabledSteps}
      currentStep={currentStep}
    />
  )
}

export default Step3
